import React, { useEffect, useState } from 'react';
import {
	Button,
	TextField,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Container,
	Alert,
	Switch,
	Typography,
	Snackbar,
	Box,
	InputAdornment,
	CircularProgress,
	TablePagination,
	CardMedia,
	Tooltip,
	useMediaQuery,
	useTheme,
	Grid,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import CreateMenu from './CreateMenu';
import { deleteMenu, getAllMenus, updateMenu, createMenu } from '../../../services/api/Menus';

interface MenuItemType {
	_id?: string;
	menuType: string;
	numberOfPersons: number;
	menuItems: any;
}

interface MenuImage {
	url: string;
	file?: File;
}

interface MenuCardProps {
	_id: string;
	status: any;
	name: string;
	eventType: any;
	description: string;
	complementary: { item: string; quantity: number }[];
	menuTypes: MenuItemType[];
	price?: number;
	menuImage?: MenuImage[];
	slug?: any;
	createdAt?: string;
	updatedAt?: string;
	createdBy?: {
		_id: string;
		firstName: string;
		lastName: string;
	};
}

const sampleMenus: MenuCardProps[] = [
	{
		_id: '1',
		name: 'Breakfast Combo',
		description: 'A wholesome breakfast combo with eggs, toast, and juice.',
		complementary: [{ item: 'candy and Popcon', quantity: 100 }],
		eventType: '',
		menuTypes: [
			{
				menuType: 'Breakfast',
				numberOfPersons: 2,
				menuItems: { Items: ['Eggs', 'Toast', 'Juice'] },
			},
		],
		createdAt: '2024-01-15',
		status: 1,
	},
];

const Menu = () => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

	const [menus, setMenus] = useState<MenuCardProps[]>([]);
	const [searchTerm, setSearchTerm] = useState('');
	const [openDialog, setOpenDialog] = useState(false);
	const [currentMenu, setCurrentMenu] = useState<MenuCardProps | null>(null);
	const [alertMessage, setAlertMessage] = useState<string | null>(null);
	const [alertSeverity, setAlertSeverity] = useState<'success' | 'error'>('success');
	const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
	const [selectedMenu, setSelectedMenu] = useState<MenuCardProps | null>(null);
	const [loading, setLoading] = useState(true);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [menuToDelete, setMenuToDelete] = useState<string | null>(null);
	const [deleting, setDeleting] = useState(false);
	const [createLoading, setCreateaLoading] = useState(false);
	const handleAddMenu = () => {
		setOpenDialog(true);
		setCurrentMenu(null);
	};

	const handleEditMenu = (menu: MenuCardProps) => {
		setCurrentMenu(menu);
		setOpenDialog(true);
	};

	const handleDeleteClick = (id: string) => {
		setMenuToDelete(id);
		setDeleteDialogOpen(true);
	};

	const handleCloseDeleteDialog = () => {
		setDeleteDialogOpen(false);
		setMenuToDelete(null);
	};

	const handleConfirmDelete = async () => {
		if (!menuToDelete) return;

		try {
			setDeleting(true);
			const response = await deleteMenu(menuToDelete);
			if (response.success) {
				setMenus((prev) => prev.filter((menu) => menu._id !== menuToDelete));
				setAlertMessage('Menu deleted successfully!');
				setAlertSeverity('success');
			} else {
				setAlertMessage('Failed to delete menu.');
				setAlertSeverity('error');
			}
		} catch (error) {
			setAlertMessage('Error deleting menu.');
			setAlertSeverity('error');
		} finally {
			setDeleting(false);
			handleCloseDeleteDialog();
		}
	};

	const handleCloseDialog = () => {
		setOpenDialog(false);
		setCurrentMenu(null);
	};

	const handleOpenDetails = (menu: MenuCardProps) => {
		setSelectedMenu(menu);
		setOpenDetailsDialog(true);
	};

	const handleCloseDetails = () => {
		setOpenDetailsDialog(false);
		setSelectedMenu(null);
	};

	// const convertToFormData = (data: any) => {
	// 	console.log(data);
	// 	const formData = new FormData();
	// 	Object.keys(data).forEach((key) => {
	// 		if (
	// 			key !== 'menuImage' &&
	// 			key !== 'menuTypes' &&
	// 			key !== '_id' &&
	// 			key !== 'complementary'
	// 		) {
	// 			formData.append(key, String(data[key] || ''));
	// 		}
	// 	});

	// 	if (data.menuImage && Array.isArray(data.menuImage)) {
	// 		data.menuImage.forEach((image: any) => {
	// 			if (image.file instanceof File) {
	// 				formData.append('menuImage', image.file);
	// 			} else if (image.url) {
	// 				formData.append('menuImage', image.url);
	// 			}
	// 		});
	// 	}

	// 	if (data.menuTypes && data.menuTypes.length > 0) {
	// 		console.log(data.menuTypes);
	// 		formData.append('menuTypes', JSON.stringify(data.menuTypes));
	// 	}
	// 	if (data.complementary && data.complementary.length > 0) {
	// 		formData.append('complementary', JSON.stringify(data.complementary));
	// 	}
	// 	console.log(formData, "formData>>>>>>>>>>>>>>>>>>>");

	// 	return formData;
	// };

	const convertToFormData = async (data: any) => {
		console.log(data);
		const formData = new FormData();
		Object.keys(data).forEach((key) => {
			if (
				key !== 'menuImage' &&
				key !== 'menuTypes' &&
				key !== '_id' &&
				key !== 'complementary'
			) {
				formData.append(key, String(data[key] || ''));
			}
		});

		if (data.menuImage && Array.isArray(data.menuImage)) {
			for (const image of data.menuImage) {
				if (image.file instanceof File) {
					formData.append('menuImage', image.file);
				} else if (image.url) {
					try {
						// Fetch the image from the URL
						const response = await fetch(image.url);
						const blob = await response.blob();
						// Extract filename from URL or generate one
						const filename = image.url.split('/').pop() || 'image.jpg';
						// Create a File object from the blob
						const file = new File([blob], filename, { type: blob.type });
						formData.append('menuImage', file);
					} catch (error) {
						console.error('Error fetching image:', error);
						// Fallback to URL as string if fetch fails
						formData.append('menuImage', image.url);
					}
				}
			}
		}

		if (data.menuTypes && data.menuTypes.length > 0) {
			console.log(data.menuTypes);
			formData.append('menuTypes', JSON.stringify(data.menuTypes));
		}
		if (data.complementary && data.complementary.length > 0) {
			formData.append('complementary', JSON.stringify(data.complementary));
		}
		console.log(formData, 'formData>>>>>>>>>>>>>>>>>>>');

		return formData;
	};
	const handleFormSubmit = async (data: any) => {
		try {
			setLoading(true);
			setCreateaLoading(true);
			const formData = await convertToFormData(data);

			const response = data._id
				? await updateMenu(formData, data._id)
				: await createMenu(formData);
			const datavalues = await response.json();

			if (datavalues.success) {
				setAlertMessage(
					` ${data._id ? datavalues.data.message : datavalues.data.message} `
				);
				setAlertSeverity('success');
				fetchData();
				setMenus((prev) =>
					data._id
						? prev.map((menu) => (menu._id === datavalues._id ? datavalues.data : menu))
						: [response.data, ...prev]
				);
			} else {
				setAlertMessage(
					response?.message || `Failed to ${data._id ? 'update' : 'create'} menu`
				);
				setAlertSeverity('error');
			}

			setOpenDialog(false);
		} catch (error) {
			console.error('Submission error:', error);
			setAlertMessage(`Error ${data._id ? 'updating' : 'creating'} menu`);
			setAlertSeverity('error');
		} finally {
			setLoading(false);
			setCreateaLoading(false);
		}
	};

	const handleToggleStatus = async (menu: any, id: string) => {
		const data = menu;
		console.log(menu, 'data');

		try {
			const formData = new FormData();
			Object.keys(data).forEach((key) => {
				if (
					key !== 'menuImage' &&
					key !== 'menuTypes' &&
					key !== '_id' &&
					key !== 'complementary' &&
					key !== 'eventType' &&
					key !== 'status'
				) {
					formData.append(key, String(data[key] || ''));
				}
			});
			formData.append('eventType', data.eventType._id);
			formData.append('status', String(data?.status == 1 ? 0 : 1));

			if (data.menuImage && Array.isArray(data.menuImage)) {
				for (const image of data.menuImage) {
					if (image.file instanceof File) {
						formData.append('menuImage', image.file);
					} else if (image.url) {
						try {
							// Fetch the image from the URL
							const response = await fetch(image.url);
							const blob = await response.blob();
							// Extract filename from URL or generate one
							const filename = image.url.split('/').pop() || 'image.jpg';
							// Create a File object from the blob
							const file = new File([blob], filename, { type: blob.type });
							formData.append('menuImage', file);
						} catch (error) {
							console.error('Error fetching image:', error);
							// Fallback to URL as string if fetch fails
							formData.append('menuImage', image.url);
						}
					}
				}
			}

			if (data.menuTypes && data.menuTypes.length > 0) {
				console.log(data.menuTypes);
				formData.append('menuTypes', JSON.stringify(data.menuTypes));
			}
			if (data.complementary && data.complementary.length > 0) {
				formData.append('complementary', JSON.stringify(data.complementary));
			}

			const response = await updateMenu(formData, id);
			const valuesdata = await response.json();

			if (valuesdata.success) {
				setAlertMessage(valuesdata.data.message);
				setMenus((prev) =>
					prev.map((menu) =>
						menu._id === id ? { ...menu, status: menu.status == 1 ? 0 : 1 } : menu
					)
				);
				setAlertSeverity('success');
			} else {
				setAlertMessage(response.data.message);
				setAlertSeverity('error');
			}
		} catch (error) {
			console.error('Error updating menu:', error);
			setAlertMessage('An error occurred while updating the menu');
			setAlertSeverity('error');
		}
	};

	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const fetchData = async () => {
		try {
			setLoading(true);
			const response = await getAllMenus();

			if (response.success) {
				console.log(response, 'response');
				setMenus(mapApiResponseToMenuCardProps(response));
			} else {
				setMenus(sampleMenus);
				setAlertMessage(response.message || 'Failed to fetch menus. Showing sample data.');
				setAlertSeverity('error');
			}
		} catch (error) {
			setMenus(sampleMenus);
			setAlertMessage('Error fetching menus. Showing sample data.');
			setAlertSeverity('error');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchData();
	}, []);

	const filteredMenus = menus?.filter((menu) =>
		menu?.name?.toLowerCase()?.startsWith(searchTerm.toLowerCase())
	);

	const mapApiResponseToMenuCardProps = (apiResponse: any): MenuCardProps[] =>
		apiResponse.data.menus.reverse()?.map((menu: any) => ({
			_id: menu._id,
			name: menu.name,
			description: menu.description,
			complementary: menu.complementary.map((comp: any) => ({
				item: comp.item,
				quantity: comp.quantity,
			})),
			menuTypes: menu.menuTypes.map((menuItem: any) => ({
				menuType: menuItem.menuType,
				numberOfPersons: menuItem.numberOfPersons,
				menuItems: menuItem.menuItems,
			})),
			price: menu.price,
			// eventType: menu.eventType._id,
			menuImage: menu.menuImage,
			slug: menu.slug,
			createdAt: menu.createdAt,
			updatedAt: menu.updatedAt,
			createdBy: menu.createdBy,
			eventType: menu.eventType,
			status: menu.status,
		}));

	return (
		<Container maxWidth={false} sx={{ width: '100%', pt: 3, pb: 4 }}>
			<Box
				sx={{
					display: 'flex',
					flexDirection: isMobile ? 'column' : 'row',
					justifyContent: 'space-between',
					alignItems: isMobile ? 'flex-start' : 'center',
					gap: isMobile ? 2 : 0,
					marginBottom: '20px',
				}}
			>
				<Typography
					variant="body2"
					sx={{
						fontFamily: 'Poppins',
						fontWeight: '400',
						color: 'hsla(215, 16%, 47%, 1)',
					}}
				>
					Manage your Menus, add new Menus, and more.
				</Typography>

				<Button
					variant="contained"
					color="primary"
					onClick={handleAddMenu}
					fullWidth={isMobile}
					size={isMobile ? 'medium' : 'large'}
				>
					Add Fixed Menu
				</Button>
			</Box>

			<Grid container spacing={2} sx={{ mb: 2 }}>
				<Grid item xs={12} sm={8} md={6} lg={4}>
					<TextField
						label="Search Menus"
						variant="outlined"
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						size="small"
						fullWidth
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon />
								</InputAdornment>
							),
						}}
					/>
				</Grid>
			</Grid>

			{loading ? (
				<Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
					<CircularProgress />
				</Box>
			) : (
				<>
					<TableContainer
						component={Paper}
						sx={{
							width: '100%',
							overflowX: 'auto',
							maxHeight: isMobile ? '400px' : 'none',
						}}
					>
						<Table stickyHeader={isMobile}>
							<TableHead>
								<TableRow>
									<TableCell sx={{ minWidth: isMobile ? '120px' : '150px' }}>
										Name
									</TableCell>
									{!isMobile && !isTablet && (
										<>
											<TableCell>Description</TableCell>
											<TableCell>Complementary</TableCell>
										</>
									)}
									{/* {isTablet && <TableCell>Complementary</TableCell>} */}
									<TableCell>Status</TableCell>
									<TableCell>Actions</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{filteredMenus.length > 0 ? (
									filteredMenus
										.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
										.map((menu) => (
											<TableRow key={menu._id}>
												<TableCell>
													<Typography
														onClick={() => handleOpenDetails(menu)}
														sx={{
															cursor: 'pointer',
															'&:hover': {
																textDecoration: 'underline',
																color: 'primary.main',
															},
														}}
													>
														{menu.name}
													</Typography>
												</TableCell>
												{!isMobile && !isTablet && (
													<>
														<TableCell
															sx={{
																maxWidth: '200px',
																whiteSpace: 'nowrap',
																overflow: 'hidden',
																textOverflow: 'ellipsis',
															}}
														>
															{menu.description}
														</TableCell>
														<TableCell>
															{menu.complementary
																.map((comp) => comp.item)
																.join(', ')}
														</TableCell>
													</>
												)}
												<TableCell>
													<Tooltip
														title={
															menu?.status == 1
																? 'Active'
																: 'Inactive'
														}
													>
														<Switch
															checked={menu?.status == 1}
															onChange={() =>
																handleToggleStatus(menu, menu._id)
															}
															color="primary"
															size={isMobile ? 'small' : 'medium'}
														/>
													</Tooltip>
												</TableCell>
												<TableCell>
													<Box sx={{ display: 'flex' }}>
														<Tooltip title="Edit">
															<IconButton
																onClick={() => handleEditMenu(menu)}
																size={isMobile ? 'small' : 'medium'}
															>
																<EditIcon
																	fontSize={
																		isMobile
																			? 'small'
																			: 'medium'
																	}
																/>
															</IconButton>
														</Tooltip>
														<Tooltip title="Delete">
															<IconButton
																onClick={() =>
																	handleDeleteClick(menu._id)
																}
																size={isMobile ? 'small' : 'medium'}
															>
																<DeleteIcon
																	fontSize={
																		isMobile
																			? 'small'
																			: 'medium'
																	}
																/>
															</IconButton>
														</Tooltip>
													</Box>
												</TableCell>
											</TableRow>
										))
								) : (
									<TableRow>
										<TableCell colSpan={isMobile ? 3 : 5} align="center">
											No menus found
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>
					<TablePagination
						rowsPerPageOptions={isMobile ? [5, 10] : [5, 10, 25]}
						component="div"
						count={filteredMenus.length}
						rowsPerPage={rowsPerPage}
						page={page}
						onPageChange={handleChangePage}
						onRowsPerPageChange={handleChangeRowsPerPage}
						sx={{
							'& .MuiTablePagination-toolbar': {
								flexWrap: 'wrap',
								justifyContent: 'center',
							},
						}}
					/>
				</>
			)}

			<Dialog
				open={openDialog}
				onClose={handleCloseDialog}
				maxWidth={isMobile ? 'sm' : 'md'}
				fullWidth
				fullScreen={isMobile}
			>
				<DialogTitle>
					{currentMenu ? 'Edit Menu' : 'Add New Menu'}
					<IconButton
						aria-label="close"
						onClick={handleCloseDialog}
						sx={{
							position: 'absolute',
							right: 8,
							top: 8,
							color: (theme) => theme.palette.grey[500],
						}}
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent>
					<CreateMenu menu={currentMenu} onSubmit={handleFormSubmit} />
				</DialogContent>
				<DialogActions sx={{ px: 3, py: 2 }}>
					<Button
						onClick={handleCloseDialog}
						variant="outlined"
						sx={{
							'&:hover': {
								backgroundColor: 'primary.main',
								color: 'white',
							},
						}}
					>
						Cancel
					</Button>
					<Button
						form="menu-form"
						type="submit"
						variant="contained"
						color="primary"
						sx={{ ml: 2 }}
						loading={createLoading}
						loadingIndicator={'Loading...'}
					>
						{currentMenu ? 'Update Menu' : 'Create Menu'}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={openDetailsDialog}
				onClose={handleCloseDetails}
				maxWidth={isMobile ? 'sm' : 'md'}
				fullWidth
				fullScreen={isMobile}
				sx={{
					'& .MuiDialog-paper': {
						borderRadius: isMobile ? 0 : '12px',
						background: '#f9fafb',
					},
				}}
			>
				<DialogTitle>
					Menu Details
					<IconButton
						aria-label="close"
						onClick={handleCloseDetails}
						sx={{
							position: 'absolute',
							right: 8,
							top: 8,
							color: (theme) => theme.palette.grey[500],
						}}
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent sx={{ py: 4, px: isMobile ? 2 : 4 }}>
					{selectedMenu && (
						<div>
							<Container maxWidth="md" sx={{ mb: 4 }}>
								<Grid container spacing={3}>
									<Grid item xs={12} sm={6} md={6}>
										<Typography
											variant="subtitle1"
											sx={{
												backgroundColor: 'white',
												borderRadius: '8px',
												padding: '16px',
												boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
												height: '100%',
											}}
											gutterBottom
										>
											<Box
												component="span"
												sx={{
													color: 'text.secondary',
													fontSize: '0.75rem',
													textTransform: 'uppercase',
													letterSpacing: '0.5px',
													mb: 0.5,
												}}
											>
												Menu Name
											</Box>
											<Box
												sx={{
													fontWeight: 500,
													fontSize: '1rem',
													color: 'text.primary',
												}}
											>
												{selectedMenu.name}
											</Box>
										</Typography>
									</Grid>

									<Grid item xs={12} sm={6} md={6}>
										<Typography
											variant="subtitle1"
											sx={{
												backgroundColor: 'white',
												borderRadius: '8px',
												padding: '16px',
												boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
												height: '100%',
											}}
											gutterBottom
										>
											<Box
												component="span"
												sx={{
													color: 'text.secondary',
													fontSize: '0.75rem',
													textTransform: 'uppercase',
													letterSpacing: '0.5px',
													mb: 0.5,
												}}
											>
												Created On
											</Box>
											<Box
												sx={{
													fontWeight: 500,
													fontSize: '1rem',
													color: 'text.primary',
												}}
											>
												{selectedMenu.createdAt
													? new Date(
															selectedMenu.createdAt
														).toLocaleDateString()
													: 'N/A'}
											</Box>
										</Typography>
									</Grid>
								</Grid>
							</Container>

							<Box
								sx={{
									backgroundColor: 'white',
									borderRadius: '8px',
									padding: '16px',
									boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
									mb: 4,
								}}
							>
								<Typography
									variant="subtitle1"
									sx={{
										color: 'text.secondary',
										fontSize: '0.75rem',
										textTransform: 'uppercase',
										letterSpacing: '0.5px',
										mb: 1,
									}}
								>
									Description
								</Typography>
								<Typography variant="body1" sx={{ color: 'text.primary' }}>
									{selectedMenu.description}
								</Typography>
							</Box>

							{selectedMenu.complementary && (
								<Box
									sx={{
										backgroundColor: 'white',
										borderRadius: '8px',
										padding: '16px',
										boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
										mb: 4,
									}}
								>
									<Typography
										variant="subtitle1"
										sx={{
											color: 'text.secondary',
											fontSize: '0.75rem',
											textTransform: 'uppercase',
											letterSpacing: '0.5px',
											mb: 1,
										}}
									>
										Complementary Items
									</Typography>
									<Typography variant="body1" sx={{ color: 'text.primary' }}>
										{selectedMenu.complementary.map((comp) => (
											<div key={comp.item}>
												{comp.item} - {comp.quantity}
											</div>
										))}
									</Typography>
								</Box>
							)}

							<Typography
								variant="h6"
								sx={{
									mt: 4,
									mb: 3,
									fontWeight: 600,
									color: 'primary.main',
									display: 'flex',
									alignItems: 'center',
									gap: 1,
								}}
							>
								Menu Items
							</Typography>

							{selectedMenu?.menuTypes.map((menuItem: any, menuIndex: any) => (
								<Box key={menuIndex} sx={{ mb: 4 }}>
									<Typography
										variant="subtitle1"
										sx={{
											fontWeight: 500,
											mb: 2,
											color: 'primary.dark',
										}}
									>
										{menuItem.menuType} (For {menuItem.numberOfPersons} persons)
									</Typography>
									<TableContainer
										component={Paper}
										sx={{
											borderRadius: '8px',
											boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
											mb: 2,
										}}
									>
										<Table>
											<TableHead>
												<TableRow
													sx={{
														backgroundColor: 'primary.light',
														'& th': {
															fontWeight: 600,
															color: 'primary.contrastText',
														},
													}}
												>
													<TableCell>Item</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{menuItem?.menuItems?.Items.map(
													(item: any, itemIndex: any) => (
														<TableRow
															key={itemIndex}
															sx={{
																'&:nth-of-type(even)': {
																	backgroundColor: 'action.hover',
																},
															}}
														>
															<TableCell>{item}</TableCell>
														</TableRow>
													)
												)}
											</TableBody>
										</Table>
									</TableContainer>
								</Box>
							))}

							{selectedMenu.menuImage && selectedMenu.menuImage.length > 0 && (
								<>
									<Typography
										variant="h6"
										sx={{
											mt: 4,
											mb: 3,
											fontWeight: 600,
											color: 'primary.main',
										}}
									>
										Menu Images
									</Typography>
									<Grid container spacing={2}>
										{selectedMenu.menuImage.map((image, index) => (
											<Grid item xs={12} sm={6} md={4} key={index}>
												<CardMedia
													component="img"
													image={
														typeof image === 'string'
															? image
															: image.url
													}
													alt={`Menu ${index + 1}`}
													sx={{
														width: '100%',
														height: isMobile ? '150px' : '200px',
														objectFit: 'cover',
														borderRadius: '8px',
														boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
													}}
												/>
											</Grid>
										))}
									</Grid>
								</>
							)}
						</div>
					)}
				</DialogContent>
				<DialogActions sx={{ px: isMobile ? 2 : 4, py: 2 }}>
					<Button
						onClick={handleCloseDetails}
						variant="outlined"
						sx={{
							'&:hover': {
								backgroundColor: 'primary.main',
								color: 'white',
							},
						}}
					>
						Close
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={deleteDialogOpen}
				onClose={handleCloseDeleteDialog}
				maxWidth="xs"
				fullWidth
				PaperProps={{
					style: {
						borderRadius: isMobile ? 0 : '12px',
					},
				}}
				fullScreen={isMobile}
			>
				<DialogTitle sx={{ fontFamily: 'Poppins', fontWeight: 600 }}>
					Confirm Delete
				</DialogTitle>
				<DialogContent>
					<Typography variant="body1" sx={{ fontFamily: 'Poppins', mt: 2 }}>
						Are you sure you want to delete this menu? This action cannot be undone.
					</Typography>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 3 }}>
					<Button
						onClick={handleCloseDeleteDialog}
						color="primary"
						variant="outlined"
						disabled={deleting}
					>
						Cancel
					</Button>
					<Button
						onClick={handleConfirmDelete}
						color="error"
						variant="contained"
						disabled={deleting}
						startIcon={
							deleting ? (
								<CircularProgress size={20} color="inherit" />
							) : (
								<DeleteIcon />
							)
						}
						sx={{ minWidth: '100px' }}
					>
						{deleting ? 'Deleting...' : 'Delete'}
					</Button>
				</DialogActions>
			</Dialog>

			<Snackbar
				open={Boolean(alertMessage)}
				autoHideDuration={3000}
				onClose={() => setAlertMessage(null)}
				anchorOrigin={{
					vertical: isMobile ? 'top' : 'bottom',
					horizontal: isMobile ? 'center' : 'right',
				}}
			>
				<Alert
					severity={alertSeverity}
					variant="filled"
					onClose={() => setAlertMessage(null)}
					sx={{ width: isMobile ? '90%' : '100%' }}
				>
					{alertMessage}
				</Alert>
			</Snackbar>
		</Container>
	);
};

export default Menu;
